/**
 * Task Type Icon & Label Utilities
 *
 * Thin façade over the canonical {@link TASK_TYPE_CONFIG} so existing
 * consumers (inbox panels, kanban board, task-type badge, …) keep working.
 *
 * Brand compliance: the `map` type no longer maps to amber `warning`. It uses
 * the informative blue intent — amber/yellow is not part of the brand palette.
 */

import { TaskType } from '@shared/models/task-type.enum';
import {
  TASK_TYPE_CONFIG,
  getTaskTypeConfig,
  normalizeTaskType,
} from '@features/tasks/domain/task-type.config';

export type TaskTypeTone = 'info' | 'constructive';

interface TaskTypeMeta {
  icon: string;
  label: string;
  /** NgZorro semantic color name (`processing` = blue, `success` = green) */
  color: string;
  tone: TaskTypeTone;
}

const TONE_BY_TYPE: Record<TaskType, TaskTypeTone> = {
  [TaskType.GENERAL]: 'info',
  [TaskType.MAP]: 'info',
  [TaskType.GEOPOINT]: 'constructive',
};

const NZ_COLOR_BY_TYPE: Record<TaskType, string> = {
  [TaskType.GENERAL]: 'processing',
  [TaskType.MAP]: 'processing',
  [TaskType.GEOPOINT]: 'success',
};

/**
 * Backwards-compatible lookup table keyed by lower-cased type name.
 */
export const TASK_TYPE_ICONS: Record<string, TaskTypeMeta> = Object.fromEntries(
  Object.entries(TASK_TYPE_CONFIG).map(([key, config]) => {
    const type = key as TaskType;
    return [
      key.toLowerCase(),
      {
        icon: config.icon,
        label: config.label,
        color: NZ_COLOR_BY_TYPE[type],
        tone: TONE_BY_TYPE[type],
      },
    ];
  }),
);

/**
 * Get icon name for a task type
 * @param taskType - Task type string (case-insensitive) or null
 * @returns Icon name for NgZorro nzType property
 */
export function getTaskTypeIcon(taskType: string | null | undefined): string {
  if (!taskType) return 'inbox';
  const type = taskType.toLowerCase();
  return TASK_TYPE_ICONS[type]?.icon ?? 'inbox';
}

/**
 * Get human-readable label for a task type
 * @param taskType - Task type string (case-insensitive) or null
 * @returns Label text for display
 */
export function getTaskTypeLabel(taskType: string | null | undefined): string {
  if (!taskType) return 'General';
  const type = taskType.toLowerCase();
  return TASK_TYPE_ICONS[type]?.label ?? 'General';
}

/**
 * Get color intent for a task type
 * @param taskType - Task type string (case-insensitive) or null
 * @returns NgZorro semantic color name (`processing` / `success`)
 */
export function getTaskTypeColor(taskType: string | null | undefined): string {
  const type = normalizeTaskType(taskType);
  return NZ_COLOR_BY_TYPE[type] ?? 'processing';
}

/**
 * Get semantic tone for a task type badge or pill.
 */
export function getTaskTypeTone(taskType: string | null | undefined): TaskTypeTone {
  const type = normalizeTaskType(taskType);
  return TONE_BY_TYPE[type] ?? 'info';
}

export { getTaskTypeConfig, normalizeTaskType };
